import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { secureZustandStorage } from '@/services/secure-storage.service';
import { Geolocation } from '@capacitor/geolocation';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { Device } from '@capacitor/device';
import { toast } from '@/utils/toast';
import {
  flushPositions,
  uploadPositionsInChunks,
} from '@/services/personnel-tracking-flush.service';
import { localNotificationService } from '@/services/local-notification.service';
import { lastLocationService } from '@/services/last-location.service';
import { personnelPositionSyncService } from '../offline/personnel-position-sync.service';
import type { PositionRecord } from '@/services/personnel-tracking-flush.service';
import { trackingNotificationBody } from '@/constants/honest-ui';
import { currentOriginator } from '../offline/client-id';
import { noteBridgeWrite } from '../offline/drain/drain-write-tracker';

// Re-export PositionRecord for consumers
export type { PositionRecord };

export const TRACK_TYPE_CONFIG = {
  route_clearing: { label: 'Route Clearing', color: '#f97316' },
  patrol: { label: 'Patrol', color: '#3b82f6' },
  search_rescue: { label: 'Search & Rescue', color: '#ef4444' },
  damage_survey: { label: 'Damage Survey', color: '#8b5cf6' },
  supply_run: { label: 'Supply Run', color: '#10b981' },
  other: { label: 'Other', color: '#6b7280' },
} as const;

export type TrackType = keyof typeof TRACK_TYPE_CONFIG;

const DEFAULT_FLUSH_INTERVAL = 30_000;
const FLUSH_THRESHOLD = 20;

const isNativePlatform =
  typeof window !== 'undefined' &&
  (window as any).Capacitor?.isNativePlatform;

// Module-level timer references (survive store re-creation)
let flushTimer: ReturnType<typeof setInterval> | null = null;
let batteryTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Upload a snapshot of buffered points in chunks of at most 500 (P0-15).
 *
 * Retryable failures (offline, 5xx, 401/403/408/429) go to the IndexedDB
 * buffer for personnelPositionSyncService.syncAll(). Every point keeps its
 * point_id, so a retry of a chunk the server already stored is deduplicated
 * on the server. A chunk refused with a permanent 4xx is dead-lettered: it is
 * kept in IndexedDB, flagged, and never resent, instead of being retried
 * forever. `requeue` is the last resort if IndexedDB itself fails.
 */
async function deliverPoints(
  sessionId: string,
  points: PositionRecord[],
  requeue: (points: PositionRecord[]) => void
): Promise<void> {
  const outcome = await uploadPositionsInChunks(sessionId, points, flushPositions);

  if (outcome.retry.length > 0) {
    try {
      await personnelPositionSyncService.savePointsOffline(sessionId, outcome.retry);
    } catch {
      requeue(outcome.retry);
    }
  }

  for (const dead of outcome.deadLettered) {
    try {
      await personnelPositionSyncService.saveDeadLetteredPoints(sessionId, dead.points, dead.status);
    } catch (err) {
      // Never put a permanently rejected chunk back in the live buffer: it
      // would be refused again on every flush.
      console.warn('Could not keep rejected GPS points for review', err);
    }
  }
}

interface PersonnelTrackingState {
  // --- Persisted state ---
  /** Whether GPS tracking is actively running */
  isTracking: boolean;
  /** The active session ID on the server */
  currentSessionId: string | null;
  /**
   * Who started the session (stamped when its id is first set): the drain hands
   * the session over under THIS origin, never under whoever is signed in later.
   */
  sessionOrigin: { user_id: string | null; tenant_id: string | null; device_session_id: string | null } | null;
  /** Track type: null = Mode A (location only), non-null = Mode B (trail) */
  trackType: TrackType | null;
  /** Callsign for the current session */
  callsign: string | null;
  /** ISO timestamp when session was started */
  startedAt: string | null;
  /** Total number of GPS points recorded this session */
  pointCount: number;

  // --- Non-persisted state ---
  /** Browser geolocation watchPosition ID */
  watchId: number | null;
  /** Capacitor geolocation watchPosition ID */
  capacitorWatchId: string | null;
  /** Flush interval timer ID */
  flushIntervalId: number | null;
  /** Buffered positions waiting to be flushed to the server */
  positionBuffer: PositionRecord[];
  /** Last error message */
  error: string | null;
  /** Last GPS accuracy in meters */
  lastAccuracy: number | null;
  /** Device battery percentage */
  batteryPercent: number | null;
  /** Configured flush interval in milliseconds */
  flushIntervalMs: number;

  // --- Actions ---
  startGeolocationWatch: (flushIntervalMs?: number) => Promise<void>;
  addPoint: (position: GeolocationPosition) => void;
  flush: () => Promise<void>;
  stopGeolocationWatch: () => Promise<void>;
  setCallsign: (callsign: string | null) => void;
  setTrackType: (type: TrackType | null) => void;
  setStartedAt: (time: string | null) => void;
  setCurrentSessionId: (id: string | null) => void;
  reset: () => Promise<void>;
}

const initialState = {
  isTracking: false,
  currentSessionId: null,
  sessionOrigin: null,
  trackType: null as TrackType | null,
  callsign: null as string | null,
  startedAt: null as string | null,
  pointCount: 0,
  watchId: null as number | null,
  capacitorWatchId: null as string | null,
  flushIntervalId: null as number | null,
  positionBuffer: [] as PositionRecord[],
  error: null as string | null,
  lastAccuracy: null as number | null,
  batteryPercent: null as number | null,
  flushIntervalMs: DEFAULT_FLUSH_INTERVAL,
};

export const usePersonnelTrackingStore = create<PersonnelTrackingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      startGeolocationWatch: async (flushIntervalMs?: number) => {
        const state = get();
        // Prevent double-starting
        if (state.watchId !== null || state.capacitorWatchId !== null) return;

        if (isNativePlatform) {
          // Native: request permissions first, then watch
          Geolocation.requestPermissions()
            .then((permResult) => {
              if (
                permResult.location === 'denied' ||
                permResult.coarseLocation === 'denied'
              ) {
                set({ error: 'Location permission denied' });
                toast.error(
                  'Location access is required for tracking'
                );
                return;
              }

              Geolocation.watchPosition(
                {
                  enableHighAccuracy: true,
                  timeout: 15000,
                  maximumAge: 5000,
                },
                (position, err) => {
                  if (err || !position) return;
                  get().addPoint({
                    coords: position.coords,
                    timestamp: position.timestamp,
                  } as unknown as GeolocationPosition);
                }
              ).then((capWatchId) => {
                set({ capacitorWatchId: capWatchId });
              });
            })
            .catch(() => {
              set({ error: 'Failed to request location permissions' });
              toast.error(
                'Location access is required for tracking'
              );
            });
        } else {
          // Browser fallback
          if (!navigator.geolocation) {
            set({ error: 'Geolocation not supported' });
            toast.error('Geolocation is not supported by this browser');
            return;
          }

          const watchId = navigator.geolocation.watchPosition(
            (pos) => {
              get().addPoint(pos);
            },
            (geoError) => {
              console.error('GPS error:', geoError.message);
              toast.error(`GPS error: ${geoError.message}`);
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
          );

          set({ watchId });
        }

        // Use the provided flush interval (fetched by the consumer via React Query)
        // or fall back to the store's current value or DEFAULT_FLUSH_INTERVAL
        const flushInterval = flushIntervalMs ?? get().flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL;
        set({ flushIntervalMs: flushInterval });

        // Start flush interval
        if (flushTimer) clearInterval(flushTimer);
        flushTimer = setInterval(() => {
          get().flush();
        }, flushInterval);

        // Keep screen awake during GPS tracking (critical for field ops)
        try {
          KeepAwake.keepAwake().catch(() => {});
        } catch {
          // Plugin not available on this platform
        }

        // Ongoing notification that a session is open. It does NOT keep the
        // watcher alive: the Android build has no background location and no
        // location foreground service (commit 3526be7fa), so positions stop when
        // the app leaves the screen. The responder reads this text in the shade
        // after leaving the app -- exactly then -- so it says so (P0-12) rather
        // than "Recording your position".
        localNotificationService
          .notifyTrackingActive(trackingNotificationBody(get().callsign))
          .catch(() => {});

        // Read initial battery level
        try {
          Device.getBatteryInfo().then((battery) => {
            if (battery.batteryLevel != null) {
              set({ batteryPercent: Math.round(battery.batteryLevel * 100) });
            }
          }).catch(() => {});
        } catch {
          // Device plugin not available
        }

        // Update battery every 60 seconds during tracking
        if (batteryTimer) clearInterval(batteryTimer);
        batteryTimer = setInterval(() => {
          try {
            Device.getBatteryInfo().then((battery) => {
              if (battery.batteryLevel != null) {
                set({ batteryPercent: Math.round(battery.batteryLevel * 100) });
              }
            }).catch(() => {});
          } catch {
            // Device plugin not available
          }
        }, 60_000);
      },

      addPoint: (position: GeolocationPosition) => {
        const accuracy = position.coords.accuracy;
        // Filter out low-accuracy readings (>50m)
        if (accuracy && accuracy > 50) return;

        // Mirror every accepted position into the last-known cache so other
        // callers (offline damage assessment creation, etc.) can fall back to
        // it when geolocation isn't immediately available.
        lastLocationService.recordPosition(position);

        const point: PositionRecord = {
          // Stable idempotency key for this point across every retry (P0-15).
          point_id: crypto.randomUUID(),
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy ?? undefined,
          altitude: position.coords.altitude ?? undefined,
          speed: position.coords.speed ?? undefined,
          heading: position.coords.heading ?? undefined,
          battery_percent: get().batteryPercent ?? undefined,
          recorded_at: new Date(position.timestamp).toISOString(),
        };

        set((s) => ({
          positionBuffer: [...s.positionBuffer, point],
          pointCount: s.pointCount + 1,
          lastAccuracy: point.accuracy ?? s.lastAccuracy,
        }));

        if (get().positionBuffer.length >= FLUSH_THRESHOLD) {
          get().flush();
        }
      },

      flush: async () => {
        const { positionBuffer, currentSessionId } = get();
        if (positionBuffer.length === 0 || !currentSessionId) return;

        // Take snapshot and clear buffer
        const points = [...positionBuffer];
        set({ positionBuffer: [] });

        await deliverPoints(currentSessionId, points, (unsent) => {
          // Re-add to buffer as last resort (IndexedDB unavailable). The points
          // keep their point_ids, so resending them later cannot duplicate.
          set((s) => ({ positionBuffer: [...unsent, ...s.positionBuffer] }));
        });
      },

      stopGeolocationWatch: async () => {
        const state = get();
        const pendingPoints = [...state.positionBuffer];
        const sessionId = state.currentSessionId;

        // Clear watchers
        if (isNativePlatform && state.capacitorWatchId) {
          Geolocation.clearWatch({ id: state.capacitorWatchId });
        } else if (state.watchId !== null) {
          navigator.geolocation.clearWatch(state.watchId);
        }

        if (flushTimer) {
          clearInterval(flushTimer);
          flushTimer = null;
        }

        if (batteryTimer) {
          clearInterval(batteryTimer);
          batteryTimer = null;
        }

        set({
          watchId: null,
          capacitorWatchId: null,
          flushIntervalId: null,
          positionBuffer: [],
        });

        // Allow screen to sleep if PDA zone tracking is also stopped
        try {
          import('./pda-zone.store').then(({ usePdaZoneStore }) => {
            if (!usePdaZoneStore.getState().isTracking) {
              KeepAwake.allowSleep().catch(() => {});
            }
          }).catch(() => {});
        } catch {
          // Plugin not available on this platform
        }

        // Clear the ongoing tracking notification.
        localNotificationService.clearTrackingNotification().catch(() => {});

        // Flush remaining buffer
        if (sessionId && pendingPoints.length > 0) {
          await deliverPoints(sessionId, pendingPoints, (unsent) => {
            console.error(`Could not store ${unsent.length} GPS point(s) for later upload`);
          });
        }
      },

      setCallsign: (callsign) => set({ callsign }),

      setTrackType: (trackType) => set({ trackType }),

      setStartedAt: (startedAt) => set({ startedAt }),

      setCurrentSessionId: (currentSessionId) => {
        const before = get().currentSessionId;
        set((state) => {
          if (!currentSessionId) return { currentSessionId: null, sessionOrigin: null };
          if (state.currentSessionId === currentSessionId && state.sessionOrigin) return { currentSessionId };
          const who = currentOriginator();
          return { currentSessionId, sessionOrigin: { user_id: who.userId, tenant_id: who.tenantId, device_session_id: who.deviceSessionId } };
        });
        // The handoff carries the open session (tracking.session): a change voids the drain receipt.
        if (before !== get().currentSessionId) noteBridgeWrite();
      },

      reset: async () => {
        await get().stopGeolocationWatch();
        const before = get().currentSessionId;
        set({
          ...initialState,
        });
        // The handoff carries the open session: ending it here (sign-out wipe) voids the receipt.
        if (before !== get().currentSessionId) noteBridgeWrite();
      },
    }),
    {
      name: 'personnel-tracking-store',
      storage: createJSONStorage(() => secureZustandStorage),
      partialize: (state) => ({
        isTracking: state.isTracking,
        currentSessionId: state.currentSessionId,
        sessionOrigin: state.sessionOrigin,
        trackType: state.trackType,
        callsign: state.callsign,
        startedAt: state.startedAt,
        pointCount: state.pointCount,
      }),
      onRehydrateStorage: () => {
        // This callback fires after state is rehydrated from localStorage.
        // If the user was tracking before the page reload, auto-resume the
        // geolocation watcher. This is critical for AC-10.
        return (state) => {
          if (
            state &&
            state.isTracking &&
            state.watchId === null &&
            state.capacitorWatchId === null
          ) {
            // Defer to next tick so the store is fully initialized
            setTimeout(() => {
              state.startGeolocationWatch();
            }, 0);
          }
        };
      },
    }
  )
);
